/**
 * Cinematic timelines for 绿色码头. Each story flag is advanced exactly once by the terminal
 * `setFlag` step of the timeline that owns it, so skip and play converge on the same state:
 *   gh.opening   → HARBOR_FREE_ROAM
 *   gh.merchant  → MERCHANT_TURTLE_EGG (then the scene chains gh.rain)
 *   gh.rain      → RAIN_APPROACHES
 * UNITS: `move` targets and `camera` centres are WORLD PIXELS (feet / tile centres via coords.ts).
 */
import type { TimelineData } from '@/core/types/contracts';
import type { StoryFlagId } from '@/story/flags';
import { ACTOR } from './actors';
import { GH } from '../maps/green-harbor';
import { centre, feet } from '../maps/coords';

const J = ACTOR.JIANG;
const Z = ACTOR.ZHAO;
const M = ACTOR.MERCHANT;

/** Where the two stand for the opening exchange (pier A, side by side, looking out to sea). */
export const OPENING_POS = {
  zhao: { ...feet(GH.spawns.pierStart.x, GH.spawns.pierStart.y - 3), facing: 'up' as const },
  jiangStart: { ...feet(GH.spawns.pierStart.x, GH.spawns.pierStart.y), facing: 'up' as const },
  jiangEnd: feet(GH.spawns.pierStart.x + 1, GH.spawns.pierStart.y - 3),
};

export const TL_OPENING: TimelineData = {
  id: 'gh.opening', skippable: true,
  steps: [
    { op: 'camera', ...centre(GH.spawns.pierStart.x, GH.spawns.pierStart.y - 2), ms: 0 },
    { op: 'anim', actor: Z, state: 'idle' },
    { op: 'face', actor: Z, facing: 'up' },
    { op: 'wait', ms: 700 },
    { op: 'move', actor: J, ...OPENING_POS.jiangEnd, speed: 60, wait: true },
    { op: 'face', actor: J, facing: 'left' },
    { op: 'say', dialogue: 'gh.opening' },
    { op: 'face', actor: Z, facing: 'down' },
    { op: 'face', actor: J, facing: 'down' },
    { op: 'camera', follow: true },
    { op: 'setFlag', flag: 'HARBOR_FREE_ROAM' },
  ],
};

/** Commentary beats: short, no flag; the scene marks them so order is guaranteed even if one is skipped. */
export const TL_BEAT_PIER: TimelineData = { id: 'gh.beat.pier', skippable: true, steps: [{ op: 'say', dialogue: 'gh.beat.pier' }, { op: 'setMark', mark: 'beat:pier' }] };
export const TL_BEAT_CANDLE: TimelineData = {
  id: 'gh.beat.candle', skippable: true,
  steps: [
    { op: 'face', actor: J, facing: 'up' },
    { op: 'say', dialogue: 'gh.beat.candle' },
    { op: 'setMark', mark: 'beat:candle' },
  ],
};
export const TL_BEAT_MARKET: TimelineData = {
  id: 'gh.beat.market', skippable: true,
  steps: [
    { op: 'face', actor: Z, facing: 'right' },
    { op: 'say', dialogue: 'gh.beat.market' },
    { op: 'setMark', mark: 'beat:market' },
  ],
};
export const TL_BEAT_LEAVES: TimelineData = {
  id: 'gh.beat.leaves', skippable: true,
  steps: [
    { op: 'face', actor: J, facing: 'up' },
    { op: 'say', dialogue: 'gh.beat.leaves' },
    { op: 'setMark', mark: 'beat:leaves' },
  ],
};

export const TL_MERCHANT: TimelineData = {
  id: 'gh.merchant', skippable: true,
  steps: [
    { op: 'camera', ...centre(GH.stalls.yabei.x + 2, GH.stalls.yabei.y + 2), ms: 400 },
    { op: 'face', actor: M, facing: 'down' },
    { op: 'say', dialogue: 'gh.merchant' },
    { op: 'emote', actor: J, emote: 'dots', ms: 700 },
    { op: 'face', actor: J, facing: 'right' },
    { op: 'move', actor: J, ...feet(GH.merchantWalkAway.x, GH.merchantWalkAway.y), speed: 70, wait: true },
    { op: 'face', actor: M, facing: 'down' },
    { op: 'setFlag', flag: 'MERCHANT_TURTLE_EGG' },
  ],
};

/** Positions the scene enforces before gh.rain when it is not chained straight after gh.merchant (resume). */
export const RAIN_POS = {
  jiang: { ...feet(GH.merchantWalkAway.x, GH.merchantWalkAway.y), facing: 'right' as const },
  zhao: { ...feet(GH.rainWalk.from.x, GH.rainWalk.from.y), facing: 'up' as const },
};

export const TL_RAIN: TimelineData = {
  id: 'gh.rain', skippable: true,
  steps: [
    { op: 'camera', ...centre(GH.rainWalk.from.x + 1, GH.rainWalk.from.y), ms: 500 },
    { op: 'face', actor: Z, facing: 'up' },
    { op: 'emote', actor: Z, emote: 'question', ms: 900 },
    { op: 'parallel', steps: [
      { op: 'move', actor: Z, ...feet(GH.rainWalk.to.x, GH.rainWalk.to.y), speed: 26, wait: true },
      { op: 'say', dialogue: 'gh.rain.walk' },
    ] },
    { op: 'face', actor: Z, facing: 'up' },
    { op: 'move', actor: J, ...feet(GH.rainWalk.to.x - 1, GH.rainWalk.to.y), speed: 90, wait: true },
    { op: 'face', actor: J, facing: 'right' },
    { op: 'emote', actor: J, emote: 'sweat', ms: 700 },
    { op: 'say', dialogue: 'gh.rain.talk' },
    { op: 'weather', state: 'wind', ms: 4000 },
    { op: 'face', actor: Z, facing: 'right' },
    { op: 'face', actor: J, facing: 'right' },
    { op: 'camera', follow: true },
    { op: 'setFlag', flag: 'RAIN_APPROACHES' },
  ],
};

export const GREEN_HARBOR_TIMELINES: TimelineData[] = [TL_OPENING, TL_BEAT_PIER, TL_BEAT_CANDLE, TL_BEAT_MARKET, TL_BEAT_LEAVES, TL_MERCHANT, TL_RAIN];

/**
 * Story flag each timeline works towards: the flag its terminal `setFlag` sets, or — for the
 * commentary beats — the flag that closes them. The verification driver (`converge`) only skips a
 * timeline whose flag is at or before its target, so `converge('MERCHANT_TURTLE_EGG')` never cuts the
 * chained gh.rain, and `converge('HARBOR_FREE_ROAM')` only ever skips gh.opening.
 */
export const TIMELINE_FLAG: Record<string, StoryFlagId> = {
  'gh.opening': 'HARBOR_FREE_ROAM',
  'gh.beat.pier': 'MERCHANT_TURTLE_EGG',
  'gh.beat.candle': 'MERCHANT_TURTLE_EGG',
  'gh.beat.market': 'MERCHANT_TURTLE_EGG',
  'gh.beat.leaves': 'MERCHANT_TURTLE_EGG',
  'gh.merchant': 'MERCHANT_TURTLE_EGG',
  'gh.rain': 'RAIN_APPROACHES',
};

/** Canonical order of the walking-commentary beats (P9). */
export const BEAT_ORDER = ['gh.beat.pier', 'gh.beat.candle', 'gh.beat.market', 'gh.beat.leaves'] as const;
export type BeatId = (typeof BEAT_ORDER)[number];
export const BEAT_MARKS: Record<BeatId, string> = {
  'gh.beat.pier': 'beat:pier', 'gh.beat.candle': 'beat:candle', 'gh.beat.market': 'beat:market', 'gh.beat.leaves': 'beat:leaves',
};
/** Map interaction id that auto-fires each beat (its `done:` mark is set when a beat is played by catch-up). */
export const BEAT_INTERACTIONS: Record<BeatId, string> = {
  'gh.beat.pier': 'beat-pier', 'gh.beat.candle': 'beat-candle', 'gh.beat.market': 'beat-market', 'gh.beat.leaves': 'beat-leaves',
};
